import { message } from 'antd';
import { COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS } from '../actions/compliance_request_instances_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { ROLE_TYPES } from '../utils/DataConstants';

const fetchUtils = new FetchUtils();

// Proposed backend contract: /api/v1/{buyer|supplier}/compliance/request_instances
// One instance per (compliance request, supplier) pair, created when a buyer
// publishes a request. Buyers review/approve; suppliers link documents.
const complianceRequestInstancesRestCrudThunksForBuyer = new RestCrudThunks(
	'request_instances',
	'compliance_request_instance',
	'complianceRequestInstance',
	'complianceRequestInstances',
	COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'compliance/'
);

const complianceRequestInstancesRestCrudThunksForSupplier = new RestCrudThunks(
	'request_instances',
	'compliance_request_instance',
	'complianceRequestInstance',
	'complianceRequestInstances',
	COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'compliance/'
);

export const complianceRequestInstancesRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? complianceRequestInstancesRestCrudThunksForSupplier
		: complianceRequestInstancesRestCrudThunksForBuyer;

// Shared helper for the instance action endpoints
// (approve / reject / link_document / unlink_document).
const instanceActionThunk =
	(action: string, successMessage: string, errorMessage: string) =>
	(userType) =>
	(instanceId, body = {}) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${userType}/compliance/request_instances/${instanceId}/${action}`;
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateStart({ id: instanceId }));
				fetchUtils
					.post(baseUrl + apiUrl, {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: body,
					})
					.then(parseJSON)
					.then(({ json, status }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
							reject('unauthorized');
							return;
						}
						if (d.status === 'error') {
							dispatch(
								COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateError(d.message, {
									id: instanceId,
								})
							);
							message.error(d.message || errorMessage);
							reject(d.message);
						} else {
							dispatch(COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateSuccess(d.data));
							if (successMessage) {
								message.success(successMessage);
							}
							resolve(d.data);
						}
					})
					.catch((d) => {
						dispatch(
							COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateError(d.message, {
								id: instanceId,
							})
						);
						message.error(errorMessage);
						reject(d.message);
					});
			});
		};
	};

export const approveComplianceRequestInstance = instanceActionThunk(
	'approve',
	'Compliance document approved.',
	'Failed to approve compliance document.'
);

export const rejectComplianceRequestInstance = instanceActionThunk(
	'reject',
	'Compliance document rejected.',
	'Failed to reject compliance document.'
);

export const linkComplianceDocumentToInstance = instanceActionThunk(
	'link_document',
	'Document linked to compliance request.',
	'Failed to link document.'
);

export const unlinkComplianceDocumentFromInstance = instanceActionThunk(
	'unlink_document',
	'Document unlinked from compliance request.',
	'Failed to unlink document.'
);

// Swaps the linked document for a different one in a single call (same body shape
// as link: { complianceDocumentId }). Backend PR #2269 exposes replace_document
// alongside link_document on the request-instance controller.
export const replaceComplianceDocumentOnInstance = instanceActionThunk(
	'replace_document',
	'Compliance document replaced.',
	'Failed to replace compliance document.'
);

// GET /api/v1/{role}/compliance/request_instances?complianceRequestId=&statuses=
// Resolve-only list of a request's instances, filtered by status — used by the
// Requests-tab Approval Status popover to show which suppliers sit in each
// status. Resolves the raw array (no redux) so each popover manages its own
// local state.
export const fetchComplianceRequestInstancesList = (userType) => (params) => (dispatch) =>
	new Promise((resolve, reject) => {
		fetchUtils
			.get(`${getBackendUri()}/api/v1/${userType}/compliance/request_instances`, {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				offset: 0,
				limit: 200,
				...params,
			})
			.then(parseJSON)
			.then(({ json, status }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
					reject('unauthorized');
					return;
				}
				if (json.status === 'error') {
					reject(json.message);
					return;
				}
				resolve(json.data || []);
			})
			.catch((error) => reject(error && error.message));
	});

// GET /api/v1/buyer/compliance/summary -> { totalSuppliers, nonCompliantSuppliers }
// Stored in the instances slice counts under 'complianceSummary'.
export const fetchComplianceSummary = (userType) => () => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${userType}/compliance/summary`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.countByStart('complianceSummary'));
			fetchUtils
				.get(baseUrl + apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
						reject('unauthorized');
						return;
					}
					if (d.status === 'error') {
						dispatch(
							COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.countByError(
								d.message,
								'complianceSummary'
							)
						);
						reject(d.message);
					} else {
						dispatch(
							COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.countBySuccess(
								d.data,
								'complianceSummary'
							)
						);
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(
						COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.countByError(
							d.message,
							'complianceSummary'
						)
					);
					reject(d.message);
				});
		});
	};
};
